/**
 * How a recruiter's incentive is worded on screen.
 *
 * Two figures, and the distinction between them is the whole reason the
 * ledger exists. One is provisional and can go down; the other is owed and
 * cannot. A screen that showed a single "Incentive" number would be making a
 * promise on the strength of a candidate who has not finished ninety days.
 *
 * The client's own word for the provisional one is "Tentative Incentive", and
 * it is stated here once. The server states it once too — see
 * `TENTATIVE_INCENTIVE_LABEL` in `models/enums.py`. Two spellings across two
 * screens is how the hedge gets quietly dropped from one of them.
 */

/**
 * The provisional figure's label, matching the server's constant.
 *
 * Not "Pipeline", which is what the ledger calls the row kind internally.
 * That word means something to whoever built this and nothing to a recruiter
 * deciding whether to chase a candidate.
 */
export const TENTATIVE_INCENTIVE_LABEL = 'Tentative Incentive';

/** The crystallised figure: ninety days done, and owed. */
export const EARNED_INCENTIVE_LABEL = 'Earned Incentive';

/** Earned and not yet paid — the number a recruiter actually asks about. */
export const YET_TO_RECEIVE_LABEL = 'Yet to receive';

/**
 * Whether a figure is worth putting on screen at all.
 *
 * Zero is a real answer here rather than an absence — nothing has been paid
 * to date and every balance starts there — but a row of zeroes on every
 * candidate is noise that trains people to stop reading the column. So a
 * zero renders as a dash in a table cell, while the My Day tiles show the
 * number, because there the zero *is* the answer to "what am I owed".
 */
export function hasValue(amount: string | null | undefined): boolean {
  if (amount === null || amount === undefined || amount === '') return false;
  return Number(amount) !== 0;
}

/**
 * The sentence under the tentative figure on My Day.
 *
 * Says the condition rather than the amount, because "why is this not earned
 * yet" is the question the number provokes — and the answer is the same for
 * everybody: ninety days from joining, then paid with the following month's
 * salary.
 */
export const TENTATIVE_HINT =
  'Becomes earned ninety days after the candidate joins, and is paid with '
  + 'the following month’s salary.';
